// React
import React, { memo, useCallback } from "react";

// Helpers
import { classNames } from "helpers";

// Servisni sloj
import { CORE_PACK_CODE } from "services/api/subscriptions/consts";
import { PricingPack } from "services/api/pricing/types";

// Tipovi
import { SubscriptionPackPickerProps } from "../../types";

/**
 * Odabir paketa karticama, s više odabira.
 *
 * Kvačica ostaje unutar kartice jer se kod višestrukog odabira mora vidjeti da izbori nisu
 * međusobno isključivi; sam obrub to ne govori. Klik radi po cijeloj kartici, ne samo po
 * kvadratiću.
 *
 * Core je uvijek uključen i ne da se isključiti — to je uvjet cjenika, ne odluka predstavnika.
 */
const SubscriptionPackPicker: React.FC<SubscriptionPackPickerProps> = (props) => {
  const { packs, selectedCodes, onToggle, hasUnknownCombination } = props;

  const handleToggle = useCallback(
    (pack: PricingPack, isCore: boolean, isSelected: boolean) => () => {
      if (isCore) return;

      onToggle(pack.code, !isSelected);
    },
    [onToggle]
  );

  return (
    <div>
      <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-3">
        Paketi
      </h3>

      {/* Svi paketi u jednom redu, jednakih širina; na uskom ekranu se slažu jedan ispod drugog. */}
      <div className="flex flex-col md:flex-row gap-3">
        {packs.map((pack) => {
          const isCore = pack.code === CORE_PACK_CODE || pack.isCore;
          const isSelected = selectedCodes.includes(pack.code);

          return (
            <button
              key={pack.id}
              type="button"
              onClick={handleToggle(pack, isCore, isSelected)}
              aria-pressed={isSelected}
              disabled={isCore}
              className={classNames(
                "flex-1 min-w-0 text-left border px-4 py-3 transition-colors",
                isSelected
                  ? "border-sky-600 bg-sky-50 ring-1 ring-sky-600"
                  : "border-slate-200 bg-white hover:border-slate-300",
                isCore ? "cursor-default" : ""
              )}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className={classNames(
                    "w-4 h-4 flex-shrink-0 flex items-center justify-center border",
                    isSelected
                      ? "bg-sky-600 border-sky-600"
                      : "bg-white border-slate-300"
                  )}
                >
                  {isSelected && (
                    <svg viewBox="0 0 16 16" className="w-3 h-3 fill-white" aria-hidden="true">
                      <path d="M6.2 11.3 3.4 8.5l1.1-1.1 1.7 1.7 4.3-4.3 1.1 1.1z" />
                    </svg>
                  )}
                </span>

                <span
                  className={classNames(
                    "text-sm font-semibold",
                    isSelected ? "text-sky-900" : "text-slate-700"
                  )}
                >
                  {pack.name}
                </span>
              </div>

              {/* `pl-[26px]` je širina kvačice i razmaka, da se tekst poravna s nazivom. */}
              {isCore && (
                <p className="text-xs text-slate-400 mt-1 pl-[26px]">Uvijek uključen.</p>
              )}
            </button>
          );
        })}
      </div>

      {hasUnknownCombination && (
        <p className="text-sm text-rose-600 mt-3">
          Cjenik nema cijenu za ovu kombinaciju paketa. Odaberite drugu kombinaciju ili je
          prvo dodajte u cjenik.
        </p>
      )}
    </div>
  );
};

export default memo(SubscriptionPackPicker);
